import React, { useEffect, useMemo, useRef, useState } from "react";
import "./Import.css";

// The live Association dropdown has ~161 options, so this is a type-ahead
// rather than a plain <select> (same searchable pattern as FieldSelect).
const MAX_VISIBLE = 50;

// Searchable Association picker. The user only ever sees and picks display
// NAMES — the backend translates the name to the Iris option key at payload
// time, because a Select field rejects anything but its configured options.
// Free text that matches no option therefore can never be committed here.
export default function AssociationSelect({ options, value, onChange }) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const [highlight, setHighlight] = useState(0);
	const wrapRef = useRef(null);
	const inputRef = useRef(null);

	// Close on outside click, discarding any unmatched query (the previously
	// picked value stays selected).
	useEffect(() => {
		if (!open) return undefined;
		const onDown = (e) => {
			if (wrapRef.current && !wrapRef.current.contains(e.target)) {
				setOpen(false);
				setQuery("");
			}
		};
		document.addEventListener("mousedown", onDown);
		return () => document.removeEventListener("mousedown", onDown);
	}, [open]);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return options;
		return options.filter((o) => String(o).toLowerCase().includes(q));
	}, [options, query]);

	const visible = filtered.slice(0, MAX_VISIBLE);

	useEffect(() => {
		setHighlight(0);
	}, [query, open]);

	const noMatch = open && query.trim() !== "" && filtered.length === 0;

	const pick = (option) => {
		onChange(option);
		setQuery("");
		setOpen(false);
	};

	const handleKeyDown = (e) => {
		if (e.key === "ArrowDown") {
			e.preventDefault();
			setOpen(true);
			setHighlight((h) => Math.min(h + 1, Math.max(visible.length - 1, 0)));
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			setHighlight((h) => Math.max(h - 1, 0));
		} else if (e.key === "Enter") {
			// Only ever commits a real option — never the raw typed text.
			e.preventDefault();
			if (visible[highlight]) pick(visible[highlight]);
		} else if (e.key === "Escape") {
			setOpen(false);
			setQuery("");
		}
	};

	return (
		<span className="assocSelect" ref={wrapRef}>
			<span className="assocInputRow">
				<input
					ref={inputRef}
					className={`importInput ${noMatch ? "noMatch" : ""}`}
					placeholder={value ? value : "Search associations…"}
					value={open ? query : value}
					onFocus={() => {
						setOpen(true);
						setQuery("");
					}}
					onChange={(e) => {
						setOpen(true);
						setQuery(e.target.value);
					}}
					onKeyDown={handleKeyDown}
				/>
				{value && (
					<button
						type="button"
						className="importBtn secondary small"
						title="Clear association"
						onClick={() => {
							onChange("");
							setQuery("");
							setOpen(false);
						}}>
						✕
					</button>
				)}
			</span>

			{open && (
				<div className="fieldSelectMenu">
					<div className="fieldSelectList">
						{visible.map((option, i) => (
							<div
								key={option}
								className={`fieldSelectOption ${
									i === highlight ? "selected" : ""
								}`}
								onMouseEnter={() => setHighlight(i)}
								onClick={() => pick(option)}>
								<span className="fieldSelectLabel">{option}</span>
								{String(option) === String(value) && (
									<span className="fieldSelectMeta">selected</span>
								)}
							</div>
						))}
						{noMatch && (
							<div className="fieldSelectEmpty">
								No matching association — Iris only accepts its configured
								options.
							</div>
						)}
						{filtered.length > MAX_VISIBLE && (
							<div className="fieldSelectEmpty">
								…{filtered.length - MAX_VISIBLE} more — keep typing to narrow.
							</div>
						)}
					</div>
				</div>
			)}
		</span>
	);
}
